import { useMemo, useState } from 'react';

import {
  formatCurrency,
  parseCurrency,
  sanitizeCurrencyInput,
} from '../../modules/currency';
import { computeTaxBreakdown, PRO_LABORE_PERCENTAGE } from '../../modules/taxes';

const TaxCalculatorTool = () => {
  const [valorNota, setValorNota] = useState('');
  const [copyFeedback, setCopyFeedback] = useState('');

  const valorNumerico = useMemo(() => parseCurrency(valorNota), [valorNota]);
  const breakdown = useMemo(() => computeTaxBreakdown(valorNumerico), [valorNumerico]);

  const resumo = useMemo(() => {
    if (valorNumerico <= 0) {
      return null;
    }

    return {
      valorNota: valorNumerico,
      proLabore: breakdown.totals.proLaboreBase,
      totalNotaFiscal: breakdown.totals.totalNotaFiscal,
      totalProLabore: breakdown.totals.totalProLabore,
      totalImpostos: breakdown.totals.totalImpostos,
      valorLiquido: breakdown.totals.valorLiquido,
    };
  }, [breakdown, valorNumerico]);

  const handleValorNotaChange = (event) => {
    const formatted = sanitizeCurrencyInput(event.target.value);
    setValorNota(formatted);
    setCopyFeedback('');
  };

  const handleClear = () => {
    setValorNota('');
    setCopyFeedback('');
  };

  const handleCopy = async () => {
    if (!resumo) {
      return;
    }

    try {
      if (!navigator?.clipboard) {
        throw new Error('API de área de transferência indisponível.');
      }

      const payload = JSON.stringify(resumo, null, 2);
      await navigator.clipboard.writeText(payload);
      setCopyFeedback('Resumo copiado para a área de transferência.');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (error) {
      setCopyFeedback(`Falha ao copiar: ${error.message}`);
    }
  };

  const handleExport = () => {
    if (!resumo || typeof window === 'undefined') {
      return;
    }

    const payload = JSON.stringify(
      {
        ...resumo,
        impostosNotaFiscal: breakdown.notaFiscalTaxes,
        impostosProLabore: breakdown.proLaboreTaxes,
      },
      null,
      2,
    );
    const blob = new Blob([payload], { type: 'application/json;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'calculadora-impostos.json';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
          Valor da nota fiscal
          <input
            type="text"
            value={valorNota}
            placeholder="R$ 0,00"
            onChange={handleValorNotaChange}
            className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 transition focus:border-brand-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          />
        </label>
        <p className="mt-1 text-xs text-slate-400">
          Digite apenas números, nós cuidamos da formatação automática.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleClear}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Limpar
        </button>
        <button
          type="button"
          onClick={handleCopy}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          disabled={!resumo}
        >
          Copiar resumo
        </button>
        <button
          type="button"
          onClick={handleExport}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          disabled={!resumo}
        >
          Exportar JSON
        </button>
      </div>

      {copyFeedback && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-600 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
          {copyFeedback}
        </div>
      )}

      {valorNumerico > 0 && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h3 className="text-sm font-semibold uppercase text-slate-500 dark:text-slate-400">
                Pró-labore estimado
              </h3>
              <p className="mt-2 text-2xl font-bold text-brand-primary">
                {formatCurrency(breakdown.totals.proLaboreBase)}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Considerando {Math.round(PRO_LABORE_PERCENTAGE * 100)}% do valor da nota.
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <h3 className="text-sm font-semibold uppercase text-slate-500 dark:text-slate-400">
                Totais
              </h3>
              <div className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-300">
                <div className="flex justify-between">
                  <span>Impostos nota fiscal</span>
                  <strong>{formatCurrency(breakdown.totals.totalNotaFiscal)}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Impostos pró-labore</span>
                  <strong>{formatCurrency(breakdown.totals.totalProLabore)}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Total de impostos</span>
                  <strong>{formatCurrency(breakdown.totals.totalImpostos)}</strong>
                </div>
                <div className="flex justify-between text-emerald-500">
                  <span>Valor líquido da nota</span>
                  <strong>{formatCurrency(breakdown.totals.valorLiquido)}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-slate-200 bg-white text-sm dark:border-slate-800 dark:bg-slate-900">
              <thead className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                <tr>
                  <th className="p-3 text-left font-semibold">Código</th>
                  <th className="p-3 text-left font-semibold">Imposto</th>
                  <th className="p-3 text-left font-semibold">Base</th>
                  <th className="p-3 text-left font-semibold">Percentual</th>
                  <th className="p-3 text-right font-semibold">Valor</th>
                </tr>
              </thead>
              <tbody>
                {[...breakdown.notaFiscalTaxes, ...breakdown.proLaboreTaxes].map((tax) => (
                  <tr
                    key={tax.code}
                    className="border-t border-slate-100 text-slate-600 dark:border-slate-800 dark:text-slate-300"
                  >
                    <td className="p-3 font-medium text-slate-500 dark:text-slate-300">
                      {tax.code}
                    </td>
                    <td className="p-3">{tax.name}</td>
                    <td className="p-3 text-xs text-slate-400">{tax.baseLabel}</td>
                    <td className="p-3">
                      {(tax.rate * 100).toFixed(Math.abs(tax.rate * 100) < 1 ? 4 : 2)}%
                    </td>
                    <td className="p-3 text-right font-semibold">
                      {formatCurrency(tax.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxCalculatorTool;
